import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { getUser, listMessages, sendMessage } from "../api";
import AppPage from "../components/AppPage";
import { WorkspaceScroll } from "../components/WorkspacePanel";

export default function Chat() {
  const { t } = useTranslation();
  const { uid } = useParams();
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const lastTsRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { setOther(await getUser(uid)); } catch { /* ignore */ }
    })();
  }, [uid]);

  // Initial load + polling every 3s for incremental messages
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const initial = async () => {
      try {
        const msgs = await listMessages(uid);
        if (cancelled) return;
        setMessages(msgs);
        if (msgs.length) lastTsRef.current = msgs[msgs.length - 1].created_at;
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      } catch { /* ignore */ }
    };

    const poll = async () => {
      try {
        const since = lastTsRef.current;
        const fresh = await listMessages(uid, since);
        if (cancelled) return;
        if (fresh.length) {
          setMessages((prev) => [...prev, ...fresh]);
          lastTsRef.current = fresh[fresh.length - 1].created_at;
          setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        }
      } catch { /* ignore */ }
    };

    initial();
    timer = setInterval(poll, 3000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [uid]);

  const onSend = async (e) => {
    e.preventDefault();
    const txt = text.trim();
    if (!txt || busy) return;
    setBusy(true);
    setText("");
    try {
      const msg = await sendMessage(uid, txt);
      setMessages((prev) => [...prev, msg]);
      lastTsRef.current = msg.created_at;
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  return (
    <AppPage testId="chat-page" bodyScroll="zones" toolbar={(
      <div className="d-flex align-items-center gap-3">
        <Link to="/directory" className="btn btn-sm btn-outline-secondary" title={t("chat.back")}>
          <i className="bi bi-arrow-left"></i>
        </Link>
        {other?.picture ? (
          <img src={other.picture} alt="" className="rounded-circle" width="40" height="40" style={{ objectFit: "cover" }} />
        ) : (
          <div className="rounded-circle bg-secondary-subtle d-flex align-items-center justify-content-center"
               style={{ width: 40, height: 40 }}>
            <i className="bi bi-person"></i>
          </div>
        )}
        <div>
          <div className="fw-semibold">{other?.name || t("chat.default_user")}</div>
          <div className="text-muted small">{other?.email}</div>
        </div>
      </div>
    )}>
      <WorkspaceScroll className="card card-soft p-3 d-flex flex-column" testId="chat-window">
        {messages.length === 0 && (
          <div className="text-muted text-center my-auto">{t("chat.empty")}</div>
        )}
        {messages.map(m => (
          <div key={m.message_id} className={`chat-bubble ${m.sender_id === user.user_id ? "me" : "them"}`}>
            {m.text}
          </div>
        ))}
        <div ref={endRef}></div>
      </WorkspaceScroll>

      <form onSubmit={onSend} className="d-flex gap-2 mt-3 flex-shrink-0">
        <input className="form-control" placeholder={t("chat.message_ph")} value={text}
               onChange={(e) => setText(e.target.value)} data-testid="chat-input" />
        <button className="btn btn-primary" data-testid="chat-send" disabled={busy} title={t("common.send")}>
          <i className="bi bi-send"></i>
        </button>
      </form>
    </AppPage>
  );
}
